import { useMemo } from 'react';

import {
  ItemThumbnailMap,
  SegmentThumbnailEntry,
} from '@/components/PresenceGroupCard/PresenceGroupCard';
import { findSegmentByDate } from '@/features/broadcast-schedule-program/helpers/find-segment-by-date';
import { TSegment } from '@/features/segments/types';
import { useGetMultipleSegmentThumbnails } from '@/features/video-thumbnails';
import { resolveItemId } from '@/utils/presence-groups';

type PresenceItemLike = {
  id?: string;
  tempId?: string;
  start_datetime: string;
  s_datetime?: Date;
  parameters?: { segment_id?: string | null };
};

export function useItemThumbnailMap<T extends PresenceItemLike>(
  items: T[],
  segmentItems: TSegment[] | undefined
): ItemThumbnailMap {
  const resolvedSegmentIdByItemId = useMemo(() => {
    const map: Record<string, string> = {};
    items.forEach(item => {
      const itemId = resolveItemId(item);
      const segId = item.parameters?.segment_id || (item as any).segment_id;
      if (segId) {
        map[itemId] = segId;
        return;
      }
      if (!segmentItems) return;
      const startDate = item.s_datetime || item.start_datetime;
      const segment = findSegmentByDate(segmentItems, startDate);
      if (segment) map[itemId] = segment.id;
    });
    return map;
  }, [items, segmentItems]);

  const uniqueSegmentIds = useMemo(
    () => [...new Set(Object.values(resolvedSegmentIdByItemId))],
    [resolvedSegmentIdByItemId]
  );

  const thumbnailsBySegment = useGetMultipleSegmentThumbnails(uniqueSegmentIds);

  return useMemo<ItemThumbnailMap>(() => {
    const map: ItemThumbnailMap = {};
    items.forEach(item => {
      const itemId = resolveItemId(item);
      const segId = resolvedSegmentIdByItemId[itemId];
      if (!segId) return;
      const segment = segmentItems?.find(s => s.id === segId);
      const thumbnails = thumbnailsBySegment[segId];
      if (segment && thumbnails) {
        map[itemId] = {
          thumbnails,
          startDatetime: segment.start_datetime,
        } satisfies SegmentThumbnailEntry;
      }
    });
    return map;
  }, [items, resolvedSegmentIdByItemId, thumbnailsBySegment, segmentItems]);
}
